import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db, firebaseConfigured } from '../services/firebase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [adminProfile, setAdminProfile] = useState(null);
  const [claims, setClaims] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    if (!firebaseConfigured || !auth || !db) {
      setLoading(false);
      setAuthError('Firebase environment variables are missing.');
      return undefined;
    }

    let unsubscribeProfile;
    const unsubscribeAuth = onAuthStateChanged(auth, async (nextUser) => {
      unsubscribeProfile?.();
      setUser(nextUser);
      setAdminProfile(null);
      setClaims(null);
      setAuthError('');

      if (!nextUser) {
        setLoading(false);
        return;
      }

      try {
        const token = await nextUser.getIdTokenResult(true);
        setClaims(token.claims);
        if (!token.claims.admin && !token.claims.superAdmin) {
          setAuthError('Aa account ne admin access aapel nathi.');
          await signOut(auth);
          return;
        }

        unsubscribeProfile = onSnapshot(doc(db, 'adminUsers', nextUser.uid), (snapshot) => {
          if (!snapshot.exists() || snapshot.data().active === false) {
            setAuthError('Aa admin account inactive chhe. Super Admin no contact karo.');
            signOut(auth);
            return;
          }
          setAdminProfile({ uid: snapshot.id, ...snapshot.data() });
          setLoading(false);
        }, (error) => {
          setAuthError(error.message || 'Admin profile load thai shakyo nahi.');
          setLoading(false);
        });
      } catch (error) {
        setAuthError(error.message || 'Authentication verify thai shakyu nahi.');
        setLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      unsubscribeProfile?.();
    };
  }, []);

  const login = useCallback(async (email, password) => {
    setAuthError('');
    if (!auth) throw new Error('Firebase is not configured.');
    return signInWithEmailAndPassword(auth, email, password);
  }, []);

  const logout = useCallback(() => signOut(auth), []);

  const isSuperAdmin = Boolean(claims?.superAdmin || adminProfile?.role === 'superAdmin');
  const can = useCallback((moduleKey, action = 'view') => {
    if (isSuperAdmin) return true;
    if (action === 'notify') {
      return (
        adminProfile?.permissions?.[moduleKey]?.notify === true ||
        adminProfile?.permissions?.announcements?.notify === true ||
        adminProfile?.permissions?.[moduleKey]?.publish === true ||
        adminProfile?.permissions?.[moduleKey]?.edit === true ||
        adminProfile?.permissions?.[moduleKey]?.create === true
      );
    }
    return adminProfile?.permissions?.[moduleKey]?.[action] === true;
  }, [adminProfile, isSuperAdmin]);

  const value = useMemo(() => ({
    user,
    adminProfile,
    claims,
    loading,
    authError,
    login,
    logout,
    isSuperAdmin,
    can,
  }), [adminProfile, authError, can, claims, isSuperAdmin, loading, login, logout, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider.');
  return context;
}
